import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getAnalytics, logEvent, isSupported } from 'firebase/analytics';

// Firebase configuration for admitarc-53af8 project
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "demo-api-key",
  authDomain: "admitarc-53af8.firebaseapp.com",
  projectId: "admitarc-53af8",
  storageBucket: "admitarc-53af8.appspot.com",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "demo-sender-id",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "demo-app-id",
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || "G-T1ELTX69D6"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// Initialize Analytics conditionally
let analytics = null;
isSupported().then((supported) => {
  if (supported) {
    analytics = getAnalytics(app);
    console.log('Firebase Analytics initialized');
  } else {
    console.log('Firebase Analytics not supported in this environment');
  }
});

// Analytics helper functions
export const trackEvent = (eventName, parameters = {}) => {
  if (analytics) {
    logEvent(analytics, eventName, parameters);
    console.log(`Analytics event logged: ${eventName}`, parameters);
  }
};

// Predefined event tracking functions
export const trackSchoolSelection = (schoolName) => {
  trackEvent('school_selected', {
    school: schoolName,
    timestamp: new Date().toISOString()
  });
};

export const trackStepStart = (stepNumber, stepName) => {
  trackEvent('step_started', {
    step_number: stepNumber,
    step_name: stepName,
    timestamp: new Date().toISOString()
  });
};

export const trackStepCompleted = (stepNumber, stepName, duration) => {
  trackEvent('step_completed', {
    step_number: stepNumber,
    step_name: stepName,
    duration_seconds: duration,
    timestamp: new Date().toISOString()
  });
};

export const trackEvaluationRequested = (stepNumber, stepName) => {
  trackEvent('evaluation_requested', {
    step_number: stepNumber,
    step_name: stepName,
    timestamp: new Date().toISOString()
  });
};

export const trackEvaluationCompleted = (stepNumber, stepName, rating, duration) => {
  trackEvent('evaluation_completed', {
    step_number: stepNumber,
    step_name: stepName,
    rating: rating,
    duration_seconds: duration,
    timestamp: new Date().toISOString()
  });
};

export const trackNavigationAction = (action, fromStep, toStep) => {
  trackEvent('navigation_action', {
    action: action, // 'back', 'next', 'start_over'
    from_step: fromStep,
    to_step: toStep,
    timestamp: new Date().toISOString()
  });
};

export const trackUserJourney = (completedSteps, totalTimeMinutes) => {
  trackEvent('user_journey', {
    completed_steps: completedSteps,
    total_time_minutes: totalTimeMinutes,
    completion_rate: (completedSteps / 3) * 100,
    timestamp: new Date().toISOString()
  });
};

export const trackApiUsage = (provider, success, responseTime) => {
  trackEvent('api_usage', {
    provider: provider, // 'openai', 'gemini', 'demo'
    success: success,
    response_time_ms: responseTime,
    timestamp: new Date().toISOString()
  });
};

export const trackError = (errorType, errorMessage, context) => {
  trackEvent('error_occurred', {
    error_type: errorType,
    error_message: errorMessage,
    context: context,
    timestamp: new Date().toISOString()
  });
};

export default app; 